import { useLocale, useTranslation } from '@multaqa/i18n'
import { Button } from '@multaqa/ui/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@multaqa/ui/components/ui/dialog'
import { Input } from '@multaqa/ui/components/ui/input'
import { toast } from '@multaqa/ui/components/ui/sonner'
import { useState } from 'react'
import { formatErrorMessage } from '@/lib/error-message'
import { localizedText } from '@/lib/localized-text'
import type { GroupRow } from '@/lib/platform'
import { useSchedulePurge } from './api'

/**
 * The dangerous action offboarding ends on: scheduling a suspended group for
 * purge. Offered only in `suspended`, matching the API's own rule (the
 * backend answers anything else with a 422 naming it) — a group still active
 * or already pending purge never sees the control at all.
 *
 * The confirmation types the group's slug rather than a bare "are you sure",
 * the same friction the design system asks for on an irreversible action; the
 * slug is a client-side gate only; the request itself carries no body.
 */
export function GroupPurgeAction({ group }: { group: GroupRow }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  if (group.status !== 'suspended') return null

  return (
    <>
      <Button
        type="button"
        variant="destructive"
        size="sm"
        onClick={() => setOpen(true)}
        data-testid="group-detail-purge-action"
      >
        {t('groups.detail.purge.action')}
      </Button>
      {open ? <SchedulePurgeDialog group={group} onClose={() => setOpen(false)} /> : null}
    </>
  )
}

function SchedulePurgeDialog({ group, onClose }: { group: GroupRow; onClose: () => void }) {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const schedulePurge = useSchedulePurge()
  const [typedSlug, setTypedSlug] = useState('')
  const name = localizedText(group.name, locale)
  const slug = group.slug ?? ''
  const confirmDisabled = slug === '' || typedSlug !== slug || schedulePurge.isPending

  return (
    <Dialog open onOpenChange={(next) => (!next ? onClose() : undefined)}>
      <DialogContent closeLabel={t('action.cancel')} data-testid="group-detail-purge-dialog">
        <DialogHeader>
          <DialogTitle>{t('groups.detail.purge.dialog.title', { name })}</DialogTitle>
          <DialogDescription>{t('groups.detail.purge.dialog.description')}</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <label className="grid gap-1.5" htmlFor="group-purge-slug">
            <span className="text-small text-muted-foreground">
              {t('groups.detail.purge.dialog.slugLabel')}
            </span>
            <Input
              id="group-purge-slug"
              value={typedSlug}
              onChange={(event) => setTypedSlug(event.target.value)}
              placeholder={t('groups.detail.purge.dialog.slugPlaceholder', { slug })}
              data-testid="group-detail-purge-slug-input"
            />
          </label>
        </DialogBody>
        <DialogFooter showCloseButton={false} closeLabel={t('action.cancel')}>
          <Button variant="outline" onClick={onClose} disabled={schedulePurge.isPending}>
            {t('action.cancel')}
          </Button>
          <Button
            variant="destructive"
            loading={schedulePurge.isPending}
            disabled={confirmDisabled}
            onClick={() =>
              schedulePurge.mutate(group.id, {
                onSuccess: () => {
                  toast.success(t('groups.detail.purge.dialog.success', { name }))
                  onClose()
                },
                onError: (error) => toast.error(formatErrorMessage(error)),
              })
            }
            data-testid="group-detail-purge-confirm"
          >
            {t('groups.detail.purge.dialog.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
